/**
 * Question Renderer Component
 * Polymorphic component that renders appropriate question type
 */

import React from 'react';
import { QuestionWithOptions } from '../../types/models';
import { MultipleChoiceQuestion } from './MultipleChoiceQuestion';
import { MultiSelectQuestion } from './MultiSelectQuestion';
import { TrueFalseQuestion } from './TrueFalseQuestion';
import { ShortAnswerQuestion } from './ShortAnswerQuestion';

interface QuestionRendererProps {
  question: QuestionWithOptions;
  selectedOptionIds?: string[];
  textResponse?: string;
  onChange: (response: { selectedOptionIds?: string[]; textResponse?: string }) => void;
  showFeedback?: boolean;
  isCorrect?: boolean;
  explanation?: string;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  selectedOptionIds = [],
  textResponse,
  onChange,
  showFeedback = false,
  isCorrect,
  explanation,
}) => {
  const handleMultipleChoiceChange = (ids: string[]) => {
    onChange({ selectedOptionIds: ids });
  };

  const handleShortAnswerChange = (text: string) => {
    onChange({ textResponse: text });
  };

  switch (question.question_type) {
    case 'multiple_choice':
      return (
        <MultipleChoiceQuestion
          question={question}
          selectedOptionIds={selectedOptionIds}
          onChange={handleMultipleChoiceChange}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
          explanation={explanation}
        />
      );

    // Matching questions use multi-select for now
    case 'multi_select':
    case 'matching':
      return (
        <MultiSelectQuestion
          question={question}
          selectedOptionIds={selectedOptionIds}
          onChange={handleMultipleChoiceChange}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
          explanation={explanation}
        />
      );

    case 'true_false':
      return (
        <TrueFalseQuestion
          question={question}
          selectedOptionIds={selectedOptionIds}
          onChange={handleMultipleChoiceChange}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
          explanation={explanation}
        />
      );

    case 'short_answer':
      return (
        <ShortAnswerQuestion
          question={question}
          textResponse={textResponse}
          onChange={handleShortAnswerChange}
          showFeedback={showFeedback}
          isCorrect={isCorrect}
          explanation={explanation}
        />
      );

    default:
      return <div>Unsupported question type: {question.question_type}</div>;
  }
};
